import React, { useState, useEffect } from 'react';

import DeviceDetails from '../components/DeviceDetails/DeviceDetails';

import { useService } from '../JOKI';
import { useSelectedDevice } from '../services/DeviceService/DevicesHooks';
import AzureMap from '../components/Map/AzureMap';
import MapLegend from '../components/Map/MapLegend';

import { notice } from '../services/Notifications/notice';

import deviceTypeIdToText from '../utils/deviceTypeIdToText';

import './viewBody.scss';
import './layout.scss';

import svgDeviceTypeCabinet from '../components/DeviceCommons/DeviceIcon/icons/if20-icon.svg';
import svgDeviceForklift from '../components/DeviceCommons/DeviceIcon/icons/old-forklift.svg';
import svgDeviceBridge from '../components/DeviceCommons/DeviceIcon/icons/old-bridge.svg';
import svgDeviceReader from '../components/DeviceCommons/DeviceIcon/icons/gate-icon.svg';
import svgDeviceEdgeServer from '../components/DeviceCommons/DeviceIcon/icons/old-edge_server.svg';
import svgDeviceHammer from '../components/DeviceCommons/DeviceIcon/icons/hammer-icon.svg';
import svgDeviceLogisticsPc from '../components/DeviceCommons/DeviceIcon/icons/old-middleware.svg';
import svgDeviceHandHeld from '../components/DeviceCommons/DeviceIcon/icons/handheld-icon.svg';
import svgDeviceIntelligentInventory from '../components/DeviceCommons/DeviceIcon/icons/II-icon.svg';
import svgDviceDes from '../components/DeviceCommons/DeviceIcon/icons/des-iconsvg.svg';
import GlobalLoadingView from './GlobalLoadingView';

export default function MapView(props) {
    const [devices, trigger, , version] = useService('DevicesService');
    const isLoading = version === null ;

    // eslint-disable-next-line
    const [selectedDevice, selectionState, changeSelection] = useSelectedDevice();

    useEffect(() => {
        trigger({
            key: "updateIntervalSpeed",
            body: "fast",
        });

        return () => {
            trigger({
                key: "updateIntervalSpeed",
                body: "slow",
            }); 
        }
    }, [])

    const [markers, setMarkers] = useState([]);
    useEffect(() => {
        if (Array.isArray(devices) && devices.length > 0) {
            setMarkers(
                devices.reduce((acc, device) => {
                    if (areValidCoordinates(device.metadata.values, device.code)) {
                        const selected = selectedDevice !== null && selectedDevice.code === device.code ? true : false;

                        // const activeAlarms = Array.isArray(device.alerts)
                        //     ? device.alerts.filter(al => al.state === "ACTIVE").length
                        //     : 0;

                        const activeAlarms = device.alertCount.active || 0;

                        const markerData = {
                            id: device.code,
                            text: `<p><strong>${device.name}</strong><br /><label>Type:</label>${deviceTypeIdToText(device.type)}<br /><label>Online:</label>${
                                device.isOnline ? "<span class='online'>Online</span>" : "<span class='offline'>Offline</span>"
                            } <br /><label>Active Alarms:</label> ${activeAlarms} / ${device.alertCount.all}</p>`,
                            color: device.isOnline ? 'green' : 'gray',
                            coords: device.metadata.values,
                            onClick: () => {
                                deviceClick(device);
                            },
                            htmlContent: `<div class="device-marker ${device.isOnline ? 'online' : 'offline'} ${activeAlarms > 0 ? 'alarms' : 'no-alarms'} ${
                                selected ? 'selected' : ''
                            }"><img src="${getIconPic(device.type)}" /></div>`,
                        };

                        acc.push(markerData);
                    }

                    return acc;
                }, [])
            );
        }
        // eslint-disable-next-line
    }, [devices, selectedDevice]);

    function deviceClick(device) {
        changeSelection(device.code);
    }

    return (
        <div className="main-view">
            {isLoading && <GlobalLoadingView/>}
            <div className="view-body">
                <div className={selectionState !== 'NOT' ? 'body-fullpage partial' : 'body-fullpage'}>
                    <AzureMap htmlMarkers={markers} />
                    <MapLegend>
                        <dl>
                            <dt>
                                <div className="map-marker online">
                                    <img src={svgDeviceTypeCabinet} alt="Cabinet marker" />
                                </div>
                            </dt>
                            <dd>Device is online and has no active alerts</dd>
                        </dl>
                        <dl>
                            <dt>
                                <div className="map-marker offline">
                                    <img src={svgDeviceTypeCabinet} alt="Cabinet marker" />
                                </div>
                            </dt>
                            <dd>Device is offline and has no active alerts</dd>
                        </dl>
                        <dl>
                            <dt>
                                <div className="map-marker online alarms">
                                    <img src={svgDeviceTypeCabinet} alt="Cabinet marker" />
                                </div>
                            </dt>
                            <dd>Device is online and has active alerts</dd>
                        </dl>
                        <dl>
                            <dt>
                                <div className="map-marker offline alarms">
                                    <img src={svgDeviceTypeCabinet} alt="Cabinet marker" />
                                </div>
                            </dt>
                            <dd>Device is offline and has active alerts</dd>
                        </dl>

                        <div className="map-legend-footer">{markers.length} devices on map.</div>
                    </MapLegend>
                </div>

                {<DeviceDetails  permissions={props.permissions}/>}
            </div>
        </div>
    );
}

function getIconPic(type) {
    if (typeof type !== 'string') {
        return null;
    }

    switch (type.toLowerCase()) {
        case 'intelligentinventory':
            return svgDeviceIntelligentInventory;
        case 'cabinet':
        case 'smartcabinet':
            return svgDeviceTypeCabinet;
        case 'handheldreader':
            return svgDeviceHandHeld;
        case 'gatereader':
        case 'readingpoint':
            return svgDeviceReader;
        case 'edgeserver':
            return svgDeviceEdgeServer;
        case 'hammer':
            return svgDeviceHammer;
        case 'forklift':
            return svgDeviceForklift;
        case 'logistics/pc':
            return svgDeviceLogisticsPc;
        case 'bridge':
            return svgDeviceBridge;
        case 'encodingstation':
            return svgDviceDes;
        default:
            return null;
    }
}

function areValidCoordinates(coords, deviceCode) {
    if (!Array.isArray(coords)) {
        return false;
    }

    if (coords[0] === null || coords[1] === null) {
        return false;
    }
    if (coords[0] === '0' || coords[1] === '0') {
        return false;
    }

    if (coords[0] === 0 || coords[1] === 0) {
        return false;
    }

    if (coords[0] > 180 || coords[0] < -180) {
        notice(`Device (${deviceCode}) has invalid coordinates ${coords[0]}, ${coords[1]}`);
        return false;
    }

    if (coords[1] > 90 || coords[1] < -90) {
        notice(`Device (${deviceCode}) has invalid coordinates ${coords[0]}, ${coords[1]}`);
        return false;
    }

    return true;
}
